import { store } from '../store';
import { ACCOUNT_INITIALIZE, LOGOUT } from '../store/actions';
import jwt_decode from 'jwt-decode';

const API_SERVER = 'https://dev-backend.fcf.services';

// Margen de seguridad en segundos: renovar el token 2 minutos antes de que expire
const EXPIRATION_BUFFER_SECONDS = 120;

// Promesa única para evitar múltiples peticiones concurrentes a /users/refresh
let activeRefreshPromise = null;

/**
 * Limpia la sesión en Redux y redirige a la página de login si la sesión ya no es recuperable.
 */
export function clearSessionAndRedirect() {
  store.dispatch({
    type: ACCOUNT_INITIALIZE,
    payload: {
      isLoggedIn: false,
      user: null,
      grupo: null,
      data: null,
      token: null,
      refresh: null,
    }
  });

  if (typeof window !== 'undefined' && window.location && !window.location.pathname.includes('/auth/signin')) {
    window.location.href = '/auth/signin';
  }
}

/**
 * Comprueba si un token JWT ha expirado o está próximo a expirar (dentro del buffer de seguridad).
 */
export function isTokenExpired(token, bufferSeconds = EXPIRATION_BUFFER_SECONDS) {
  if (!token) return true;
  try {
    const decoded = jwt_decode(token);
    if (!decoded || !decoded.exp) return true;
    const now = Date.now() / 1000;
    return (now + bufferSeconds) >= decoded.exp;
  } catch {
    return true;
  }
}

/**
 * Renueva el token de sesión usando el refreshToken almacenado.
 * Implementa un bloqueo por promesa para evitar peticiones duplicadas simultáneas a Cognito.
 */
export async function renewToken() {
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = (async () => {
    const state = store.getState();
    const currentRefresh = state.account?.refresh;

    if (!currentRefresh) {
      console.warn('[auth] No hay refreshToken disponible');
      throw new Error('NO_REFRESH_TOKEN');
    }

    try {
      const res = await fetch(`${API_SERVER}/users/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: currentRefresh }),
      });

      if (!res.ok) {
        let errBody = {};
        try {
          errBody = await res.json();
        } catch (_) {}

        const msg = errBody.message || res.statusText || 'Error al renovar token';
        console.error('❌ Error respuesta al renovar token:', res.status, msg);

        const error = new Error(msg);
        error.status = res.status;
        error.isInvalidToken = res.status === 400 || res.status === 401 || (msg && msg.toLowerCase().includes('invalid refresh token'));
        throw error;
      }

      const data = await res.json();
      const idToken = data.idToken || data.token;
      if (!idToken) {
        throw new Error('La respuesta del servidor no incluye idToken');
      }

      const decodedUser = jwt_decode(idToken);
      const prevAccount = state.account || {};

      // Guardamos nuevo token y preservamos datos de usuario y el refreshToken
      store.dispatch({
        type: ACCOUNT_INITIALIZE,
        payload: {
          isLoggedIn: true,
          user: decodedUser.name || prevAccount.user || decodedUser['cognito:username'] || decodedUser.email,
          grupo: decodedUser["cognito:groups"]?.[0] || prevAccount.grupo,
          data: { ...(prevAccount.data || {}), ...decodedUser },
          token: idToken,
          refresh: currentRefresh,
        }
      });

      console.log('✅ Token renovado con éxito:', idToken.slice(0, 25) + '...');
      return idToken;
    } catch (err) {
      console.error('❌ Error renovando token:', err);
      // Si el refreshToken es inválido o expiró definitivamente, cerrar sesión
      if (err.isInvalidToken || err.message === 'NO_REFRESH_TOKEN') {
        clearSessionAndRedirect();
      }
      throw err;
    } finally {
      activeRefreshPromise = null;
    }
  })();

  return activeRefreshPromise;
}

/**
 * Obtiene un token válido. Si está expirado o próximo a expirar, lo renueva automáticamente.
 */
export async function getValidToken(force = false) {
  const state = store.getState();
  let token = state.account?.token;
  const refresh = state.account?.refresh;

  if (!token) {
    if (refresh) {
      try {
        return await renewToken();
      } catch {
        return null;
      }
    }
    return null;
  }

  if (force || isTokenExpired(token)) {
    if (refresh) {
      try {
        token = await renewToken();
      } catch (err) {
        console.warn('[auth] Error al asegurar token válido:', err.message);
        // Si no se pudo renovar pero el token actual técnicamente aún no expiró (dentro del buffer), usar el actual
        if (!isTokenExpired(token, 0)) {
          return token;
        }
        return null;
      }
    }
  }

  return token;
}

/**
 * Fetch con autenticación automática, renovación preventiva y reintento en caso de 401/403.
 */
export async function authFetch(endpoint, options = {}, isRetry = false) {
  let token = await getValidToken();

  const url = endpoint.startsWith('http')
    ? endpoint
    : `${API_SERVER}${endpoint.startsWith('/') ? '' : '/'}${endpoint}`;

  const esFormData = typeof FormData !== 'undefined' && options.body instanceof FormData;

  const headers = {
    ...(esFormData ? {} : { 'Content-Type': 'application/json' }),
    ...(options.headers || {}),
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  if (esFormData) delete headers['Content-Type'];

  const fetchOptions = {
    method: options.method || 'GET',
    headers,
    body: esFormData || typeof options.body !== 'object' || options.body === null
      ? options.body
      : JSON.stringify(options.body),
  };

  try {
    const response = await fetch(url, fetchOptions);

    // Si recibimos 401 o 403 y no es un reintento, intentamos renovar el token y reintentar una vez
    if ((response.status === 401 || response.status === 403) && !isRetry) {
      const state = store.getState();
      if (state.account?.refresh) {
        console.warn(`[authFetch] Recibido status ${response.status}. Forzando renovación de token y reintentando...`);
        try {
          const newToken = await renewToken();
          if (newToken) {
            return await authFetch(endpoint, options, true);
          }
        } catch (refreshErr) {
          console.error('[authFetch] Falló reintento con token renovado:', refreshErr);
        }
      }
    }

    // Si persiste el 401 después del reintento (o sin refresh token), la sesión ya no es válida
    if (response.status === 401) {
      console.warn('[authFetch] 401 no recuperable, redirigiendo a signin...');
      clearSessionAndRedirect();
      return response;
    }

    if (!response.ok) {
      console.warn('[authFetch] ❌ response no OK:', response.status, url);
    }

    return response;
  } catch (err) {
    console.error('[authFetch] ❌ Error de red o CORS:', err);
    throw err;
  }
}
